import type { Dispatch, SetStateAction } from 'react'
import { useCallback } from 'react'

import type { Row } from '@tanstack/react-table'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'

import { useUpdateSocialAccounts } from '../../../hooks/use-update-social-account.tsx'
import type { SocialAccount } from '../../../types'

interface SocialRemoveAccountDialogProps {
  row: Row<SocialAccount>
  showRemoveDialog: boolean
  setRemoveAccountDialog: Dispatch<SetStateAction<boolean>>
}

export const AccountRemoveDialog = ({
  row,
  showRemoveDialog,
  setRemoveAccountDialog,
}: SocialRemoveAccountDialogProps) => {
  const { id, name } = row.original

  const { deleteAccount } = useUpdateSocialAccounts()
  const { socialDisplayName } = useSocialMediaType()

  const toast = useToast()

  const handleDeleteAccount = useCallback(
    async () => {
      await deleteAccount.mutateAsync(String(id)).then(() => {
        toast.success(`Successfully deleted ${socialDisplayName} Account`)
      })

      setRemoveAccountDialog(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteAccount],
  )

  const handleCloseDialog = () => {
    setRemoveAccountDialog(false)
  }

  return (
    <Dialog show={showRemoveDialog}>
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          This action cannot be undone. This will permanently remove the
          following {socialDisplayName} Account: {name}
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.ClosableFooter
        className="mt-6"
        onClose={handleCloseDialog}
      >
        <Button
          variant="destructive"
          onClick={handleDeleteAccount}
          isLoading={deleteAccount.isLoading}
        >
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
